/**
 * White-Label Theme Provider — Phase 4b
 *
 * Resolves the "effective org" for the current session:
 *   - If an admin is acting-as a partner, the target partner
 *   - Otherwise, the user's own organization
 *
 * Then reads `organizations.custom_colors` and, when present, overrides the
 * global `--brand-accent` CSS variable family. Any component using
 * `bg-brand` / `text-brand` / `border-brand` / `hsl(var(--brand-accent))`
 * automatically picks up the partner's colors.
 *
 * Also exposes:
 *   - `hideOperatorName`: true when the effective org is a managed partner
 *     with `operator_name_hidden = true`. Consumers use this to hide the
 *     "Apex AI Labs" wordmark from footers/emails/etc.
 *   - `effectiveOrg`: { id, name, logoUrl, customColors, isManaged, operatorNameHidden }
 *     for direct consumption where the raw data is needed.
 */
import * as React from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useActingAs } from './ActingAsContext';

type CustomColors = {
  brand?: string;
  accent?: string;
  primary?: string;
} | null;

interface EffectiveOrg {
  id: string;
  name: string;
  logoUrl: string | null;
  customColors: CustomColors;
  isManaged: boolean;
  operatorNameHidden: boolean;
}

interface WhiteLabelThemeState {
  effectiveOrg: EffectiveOrg | null;
  hideOperatorName: boolean;
  loading: boolean;
}

const WhiteLabelThemeContext = React.createContext<WhiteLabelThemeState>({
  effectiveOrg: null,
  hideOperatorName: false,
  loading: true,
});

/**
 * Convert a hex color (#RRGGBB or #RGB) to Tailwind's `H S% L%` triplet.
 * Returns null on invalid input.
 */
function hexToHslTriplet(hex: string): string | null {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;

  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  let hVal = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hVal = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: hVal = ((b - r) / d + 2); break;
      case b: hVal = ((r - g) / d + 4); break;
    }
    hVal *= 60;
  }

  return `${Math.round(hVal)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Adjust lightness by delta (percentage points), clamped 0..100. */
function shiftLightness(triplet: string, delta: number): string {
  const m = /^(\d+)\s+(\d+)%\s+(\d+)%$/.exec(triplet);
  if (!m) return triplet;
  const h = m[1];
  const s = m[2];
  const l = Math.max(0, Math.min(100, parseInt(m[3], 10) + delta));
  return `${h} ${s}% ${l}%`;
}

export function WhiteLabelThemeProvider({ children }: { children: React.ReactNode }) {
  const { user, profile } = useAuth();
  const { actingAs } = useActingAs();

  const effectiveOrgId = actingAs?.orgId ?? profile?.organization_id ?? null;

  const [effectiveOrg, setEffectiveOrg] = React.useState<EffectiveOrg | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);

  React.useEffect(() => {
    let cancelled = false;

    if (!effectiveOrgId || !user) {
      setEffectiveOrg(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    (supabase
      .from('organizations')
      // The Phase 1 whiteglove columns aren't in the generated types yet;
      // cast the query builder so the select is compilable.
      .select('id, name, logo_url, custom_colors, is_managed, operator_name_hidden') as any)
      .eq('id', effectiveOrgId)
      .maybeSingle()
      .then(({ data, error }: { data: any; error: any }) => {
        if (cancelled) return;
        if (error || !data) {
          setEffectiveOrg(null);
        } else {
          setEffectiveOrg({
            id: data.id,
            name: data.name,
            logoUrl: data.logo_url ?? null,
            customColors: (data.custom_colors as CustomColors) ?? null,
            isManaged: !!data.is_managed,
            operatorNameHidden: !!data.operator_name_hidden,
          });
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [effectiveOrgId, user]);

  // Inject CSS vars scoped to <html data-whitelabel="on"> for both light/dark.
  // We override --brand-accent (used by bg-brand/text-brand/border-brand utilities
  // via tailwind config) and its light/muted siblings.
  React.useEffect(() => {
    const root = document.documentElement;
    const brandHex = effectiveOrg?.customColors?.brand ?? effectiveOrg?.customColors?.primary;
    if (!brandHex) {
      root.style.removeProperty('--brand-accent');
      root.style.removeProperty('--brand-accent-light');
      root.style.removeProperty('--brand-accent-muted');
      root.style.removeProperty('--brand');
      root.removeAttribute('data-whitelabel');
      return;
    }

    const triplet = hexToHslTriplet(brandHex);
    if (!triplet) {
      root.removeAttribute('data-whitelabel');
      return;
    }

    root.style.setProperty('--brand-accent', triplet);
    root.style.setProperty('--brand-accent-light', shiftLightness(triplet, +10));
    root.style.setProperty('--brand-accent-muted', shiftLightness(triplet, -15));
    // Keep an alias for --brand which some legacy usages rely on
    root.style.setProperty('--brand', triplet);
    root.setAttribute('data-whitelabel', 'on');

    return () => {
      // Cleanup on unmount / effective-org change handled by the next run's
      // "no brandHex" branch above.
    };
  }, [effectiveOrg?.customColors?.brand, effectiveOrg?.customColors?.primary]);

  const value = React.useMemo<WhiteLabelThemeState>(
    () => ({
      effectiveOrg,
      hideOperatorName: !!(effectiveOrg?.isManaged && effectiveOrg?.operatorNameHidden),
      loading,
    }),
    [effectiveOrg, loading],
  );

  return (
    <WhiteLabelThemeContext.Provider value={value}>{children}</WhiteLabelThemeContext.Provider>
  );
}

export function useWhiteLabelTheme() {
  return React.useContext(WhiteLabelThemeContext);
}

/** Convenience: true when the operator's own branding must be hidden. */
export function useHideOperatorName() {
  return useWhiteLabelTheme().hideOperatorName;
}
